import {
  Settings,
  Calculator,
  ClipboardCheck,
  FileText,
  BarChart3,
  Landmark,
  ShieldCheck,
  CheckCircle2,
  Receipt,
  Scale,
  Lock,
  AlertTriangle,
  FolderArchive,
  Sparkles,
} from "lucide-react";
import FeaturePageLayout from "./FeaturePageLayout";
import complianceHeroImg from "../../assets/images/features/Compliance-TDS-hero.png.png";

const workflowSteps = [
  {
    id: 1,
    title: "Configure Statutory Rules",
    desc: "Set up PF, ESI, Professional Tax and TDS rules in Brixigo statutory modules to match your organisation and location.",
    icon: Settings,
  },
  {
    id: 2,
    title: "Auto-Calculate on Payroll",
    desc: "When you process payroll, statutory deductions are computed per employee salary structure and applicable slabs.",
    icon: Calculator,
  },
  {
    id: 3,
    title: "Review Before Approval",
    desc: "Payroll administrators review PF, ESI, PT and TDS totals on the payroll run before final lock and disbursement.",
    icon: ClipboardCheck,
  },
  {
    id: 4,
    title: "Payslips & Records",
    desc: "Every statutory deduction appears on employee payslips. Payroll records are stored securely for audit readiness.",
    icon: FileText,
  },
  {
    id: 5,
    title: "Compliance Reports",
    desc: "Generate statutory and TDS reports for internal verification, filings and auditor requests from one platform.",
    icon: BarChart3,
  },
];

const keyBenefits = [
  { title: "Automatic PF calculation", desc: "Employee and employer PF computed per configured rules.", icon: Landmark },
  { title: "Automatic ESI calculation", desc: "ESI contributions applied where applicable on payroll.", icon: ShieldCheck },
  { title: "Professional Tax support", desc: "State-wise PT handled within your payroll workflow.", icon: Scale },
  { title: "TDS calculation", desc: "Income tax deducted at source aligned with employee tax setup.", icon: Receipt },
  { title: "Compliance reports", desc: "PF, ESI, PT and TDS summaries for review and filing support.", icon: BarChart3 },
  { title: "Accurate statutory deductions", desc: "Reduce manual errors with rule-driven calculations.", icon: CheckCircle2 },
  { title: "Secure payroll records", desc: "Encrypted, org-scoped storage of every payroll and deduction run.", icon: Lock },
  { title: "Reduced compliance errors", desc: "Automated compliance minimises rework before month-end close.", icon: AlertTriangle },
];

const whatsIncluded = [
  { title: "PF Configuration", desc: "EPF rules, wage ceilings and component mapping.", icon: Landmark },
  { title: "ESI Configuration", desc: "Contribution rates and eligibility checks.", icon: ShieldCheck },
  { title: "Professional Tax", desc: "Location-based PT setup and deduction.", icon: Scale },
  { title: "TDS / Income Tax", desc: "Employee tax declarations and TDS on payroll.", icon: Receipt },
  { title: "Pre-Approval Review", desc: "Verify statutory totals on each payroll run.", icon: ClipboardCheck },
  { title: "Payslip Line Items", desc: "PF, ESI, PT and TDS shown clearly on payslips.", icon: FileText },
  { title: "Audit-Ready Archives", desc: "Historical payroll runs retained for statutory reporting.", icon: FolderArchive },
  { title: "Form 16 & Tax Docs", desc: "Employee tax documents accessible from the platform.", icon: FileText },
];

export default function ComplianceTds() {
  return (
    <FeaturePageLayout
      featureName="Compliance & TDS"
      title="Compliance & TDS"
      subtitle="Stay statutory-compliant with automated PF, ESI, PT and TDS on every payroll run."
      description="Compliance is essential to payroll management. Brixigo helps organisations manage statutory obligations with automated calculations and accurate reporting — so administrators can review deductions before approval and maintain government-ready records."
      heroImage={complianceHeroImg}
      heroImageAlt="Compliance and TDS illustration"
      workflowHeading="How Compliance & TDS Works in Brixigo"
      workflowSteps={workflowSteps}
      benefits={keyBenefits}
      included={whatsIncluded}
      ctaIcon={Sparkles}
      ctaTitle="Stay Compliant. Run Payroll with Confidence."
      ctaDescription="Automate statutory calculations and keep audit-ready records without spreadsheet chaos."
      ctaButtonLabel="Book a Demo"
    />
  );
}
